import type { PrintJobSnapshot, RenderedPrintDocument, ToHtmlOptions } from "../core/types";
import { paginateElements } from "./paginateElements";
import { createPrintStyles, renderDocumentEnvelope } from "./printStyles";
import { renderPageWrapper } from "./renderElement";

function shouldUseExplicitPageWrappers(snapshot: PrintJobSnapshot, options: ToHtmlOptions): boolean {
  if ((options.mode ?? "preview") === "preview" && (options.previewMode ?? "document") === "paged") {
    return true;
  }

  // Template-style or manually paged jobs need stable per-page containers even
  // in document preview and print so positioned elements keep the correct page.
  return snapshot.elements.some((element) => element.kind === "pageBreak" || element.pageBreakBefore || element.pageBreakAfter);
}

// Build the final preview/print document. Preview can operate either as a
// continuous document or as a manually segmented paged layout.
export async function renderPrintDocument(
  snapshot: PrintJobSnapshot,
  options: ToHtmlOptions = {}
): Promise<RenderedPrintDocument> {
  const mode = options.mode ?? "preview";
  const previewMode = options.previewMode ?? "document";
  const cssText = createPrintStyles(snapshot, options);

  let body = "";
  let pageCount = 1;

  if (shouldUseExplicitPageWrappers(snapshot, options)) {
    const segments = paginateElements(snapshot.elements, {
      respectPageBreaks: options.respectPageBreaks
    });

    pageCount = segments.length;

    const pages = await Promise.all(
      segments.map((segment, index) =>
        renderPageWrapper(segment.elements, options, {
          className: snapshot.className,
          pageNumber: index + 1,
          showPageNumbers: mode === "preview" && previewMode === "paged" && options.showPageNumbers,
          forcePageBreakAfter: segment.manualBreakAfter
        })
      )
    );

    body = `<div class="printer-pages">${pages.join("")}</div>`;
  } else {
    body = await renderPageWrapper(snapshot.elements, options, {
      className: snapshot.className,
      pageNumber: 1,
      showPageNumbers: mode === "preview" && previewMode === "paged" && options.showPageNumbers
    });
  }

  return renderDocumentEnvelope(snapshot, body, cssText, pageCount);
}
